<template>
    <div>
        <h3>{{ $t("settings.channels.email.header") }}</h3>
        <!-- Always start new conversation -->
        <b-form-group
            id="fieldset-email-new-conversation"
            label-cols-sm="4"
            label-cols-lg="3"
            content-cols-sm
            content-cols-lg="7"
            label-for="doubleReplyActive"
            :description="$t('settings.channels.email.new-conversation.description')"
            :label="$t('settings.channels.email.new-conversation.label')"
        >
            <b-form-checkbox
                v-model="getSettingByName('email_alwaysStartNewConversation').value"
                name="newConversation"
                switch
                inline
                value="1"
                unchecked-value="0"
            />
        </b-form-group>

        <!-- Sender name -->
        <b-form-group
            id="fieldset-email-sender-name"
            label-cols-sm="4"
            label-cols-lg="3"
            content-cols-sm
            content-cols-lg="7"
            label-for="doubleReplyActive"
            :description="$t('settings.channels.email.sender-name.description')"
            :label="$t('settings.channels.email.sender-name.label')"
        >
            <b-input-group>
                <b-form-input
                    id="sender-name"
                    v-model="getSettingByName('email_sender').value"
                    name="sender-name"
                    type="text"
                    class="mr-50"
                />
                <b-dropdown
                    name="sender-name-variable-dropdown"
                    variant="light"
                    type="button"
                    toggle-class="text-decoration-none"
                    no-caret
                >
                    <template #button-content>
                        <font-awesome-icon :icon="['far', 'brackets-curly']" size="1x" class="cursor-pointer" />
                    </template>
                    <b-dropdown-item
                        v-for="option in variableOptions"
                        :key="`sender-name-option-${option}`"
                        @click="addVariableToSenderName(option)"
                    >
                        {{ option }}
                    </b-dropdown-item>
                </b-dropdown>
            </b-input-group>
        </b-form-group>

        <!-- Signature -->
        <b-form-group
            id="fieldset-email-signature"
            label-cols-sm="4"
            label-cols-lg="3"
            content-cols-sm
            content-cols-lg="7"
            label-for="identity"
            :description="$t('settings.channels.email.signature.description')"
            :label="$t('settings.channels.email.signature.label')"
        >
            <b-form>
                <div class="message-editor">
                    <quill-editor
                        id="quil-content"
                        ref="quill-editor"
                        v-model="getSettingByName('email_signature').value"
                        name="quil-content"
                        :options="editorOption"
                    />
                    <div id="quill-toolbar" class="d-flex">
                        <button class="ql-bold" />
                        <button class="ql-italic" />
                        <button class="ql-underline" />
                        <button class="ql-align" />
                        <button class="ql-link" />
                        <button class="ql-image" />
                        <button class="ql-list" value="ordered" />
                        <button class="ql-list" value="bullet" />
                        <select class="ql-color" :title="$t('settings.channels.email.text-color')" />
                        <button class="ql-emoji" />
                        <!-- custom variable picker -->
                        <b-dropdown
                            name="signature-variable-dropdown"
                            variant="flat-dark"
                            type="button"
                            toggle-class="text-decoration-none"
                            no-caret
                        >
                            <template #button-content>
                                <font-awesome-icon :icon="['far', 'brackets-curly']" size="1x" class="cursor-pointer" />
                            </template>
                            <b-dropdown-item
                                v-for="option in variableOptions"
                                :key="`signature-option-${option}`"
                                @click="addVariableToSignature(option)"
                            >
                                {{ option }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </div>
            </b-form>
        </b-form-group>
    </div>
</template>

<script>
import {
    BContainer,
    BCol,
    BRow,
    BCard,
    BFormGroup,
    BFormCheckbox,
    BFormTimepicker,
    BInputGroup,
    BFormDatepicker,
    BButton,
    BForm,
    BDropdown,
    BDropdownItem,
    BFormInput,
} from "bootstrap-vue";
import { Quill, quillEditor } from "vue-quill-editor";
import useChannelSettings from "../useChannelSettings";
import ImageResize from "quill-image-resize-vue";
import { addDataToInput } from "@/utils/input";
// Imports for the emoji picker
import quillEmoji from "quill-emoji";
import "quill-emoji/dist/quill-emoji.css";

// Register the resizing module allowing users to resize images they add
Quill.register("modules/imageResize", ImageResize);

export default {
    components: {
        BContainer,
        BCol,
        BRow,
        BCard,
        BFormGroup,
        BFormCheckbox,
        BFormTimepicker,
        BInputGroup,
        BFormDatepicker,
        BButton,
        BForm,
        quillEditor,
        BDropdown,
        BDropdownItem,
        BFormInput,
    },
    props: {
        channelData: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const editorOption = {
            modules: {
                toolbar: "#quill-toolbar",
                imageResize: {},
                "emoji-toolbar": true,
                "emoji-shortname": true,
            },
            placeholder: "Message",
        };
        const { getSettingByName } = useChannelSettings(props.channelData);
        const variableOptions = ["agent.fullname", "agent.displayname"];

        return {
            editorOption,
            getSettingByName,
            variableOptions,
        };
    },
    methods: {
        addVariableToSignature(variable) {
            const signature = this.getSettingByName("email_signature");
            const quillInstance = this.$refs["quill-editor"].quill;
            const caretPosition = quillInstance.selection.savedRange.index;
            quillInstance.insertText(caretPosition, `{{${variable}}}`);
        },
        addVariableToSenderName(variable) {
            const senderName = this.getSettingByName("email_sender");
            senderName.value = addDataToInput("sender-name", senderName.value, `{{${variable}}}`);
        },
    },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/quill.scss";
</style>

<style lang="scss" scoped>
form ::v-deep {
    // Quill Editor Style
    .quill-editor {
        color: black;

        .ql-container.ql-snow {
            font-size: 14px;
            font-family: sans-serif;

            border-bottom: 0;
            min-height: 200px;
        }
    }

    .ql-toolbar.ql-snow {
        border-radius: 10px;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }
}
</style>
